/*
 * partSelection.vue の WorkAndBuhin 常時表示、ref 経由 API、
 * 保存 payload、削除、初期選択、登録 action を切り出した処理。
 */
import { INSPECT_TYPE } from "@/model/InspectType";

export interface PartSelectionWorkBuhinRefAction {
  type:
    | "selectWork"
    | "noSeibi"
    | "resetBuhin"
    | "hideBuhin"
    | "reInitBuhin"
    | "showMsgConfirm"
    | "none";
  work?: any;
  index?: number;
  isManual?: boolean;
  forceSelect?: boolean;
  data?: any;
}

export interface PartSelectionSelectState {
  isInspect: boolean;
  activeInspect: any;
  activeInspectId: any;
  refAction: PartSelectionWorkBuhinRefAction;
}

export interface PartSelectionRecommendWorkAndBuhinActiveData {
  sknRecommendInspectCategoryId: any;
  sknRecommendInspectCategoryNm: any;
  status?: any;
  kingakuZn?: any;
  kingakuZk?: any;
  subItems: any[];
  seibiList: any[];
}

export interface PartSelectionInitRefActions {
  visibilityAction: PartSelectionWorkBuhinRefAction;
  selectState: PartSelectionSelectState;
}

export interface PartSelectionConfirmSaveState {
  isRecommend: boolean;
  editItem: any;
  inspectItem: any;
  changeList: any;
}

export type PartSelectionSaveRequestKind =
  | "inspectLine"
  | "recommend"
  | "inspect";

export interface PartSelectionNormalSavePayloadState {
  data: any;
  path: string;
  isSocket: number;
  shouldClearActiveInspectId: boolean;
}

export interface PartSelectionDeleteTargets {
  manualSeibiIds: any[];
  manualBuhinIds: any[];
}

export interface PartSelectionCircleDeleteApiActions {
  shouldDeleteManualItems: boolean;
  deleteTargets: PartSelectionDeleteTargets;
}

export interface PartSelectionInspectButtonChangeState {
  itemState: any;
  shouldNormalizeInspectParam: boolean;
}

export interface PartSelectionEditDialogState {
  editTitle: any;
  displayDialogEdit: boolean;
  editItem: any;
  hideTriangle: boolean;
}

export interface PartSelectionDeleteBuhinCallbackState {
  editItem: any;
  shouldSaveInspectResult: boolean;
}

export interface PartSelectionSaveSuccessActions {
  shouldSendSocket: boolean;
  shouldReload: boolean;
  shouldShowWorkBuhinConfirm: boolean;
}

export interface PartSelectionSaveLoadingActions {
  shouldStartLoading: boolean;
  shouldStopLoadingAfterRequestSetup: boolean;
}

export interface PartSelectionRegisterActions {
  shouldOpenConfirmDialog: boolean;
  shouldEmitSaveSpentTime: boolean;
  shouldEmitRegister: boolean;
  registerPayload: any;
}

export interface PartSelectionTemporarySaveActions {
  shouldEmitSaveSpentTime: boolean;
  shouldOpenSaveDialog: boolean;
}

export interface PartSelectionConfirmRegisterActions {
  shouldEmitSaveSpentTime: boolean;
  shouldEmitRegister: boolean;
  registerPayload: any;
}

export interface PartSelectionSavePartSelectionSuccessActions {
  shouldStopLoading: boolean;
  shouldInitData: boolean;
  shouldShowWorkBuhinConfirm: boolean;
}

export interface PartSelectionSavePartSelectionErrorActions {
  shouldStopLoading: boolean;
  shouldNotifyError: boolean;
  errorMessage: any;
}

export interface PartSelectionSaveRequestState {
  data: any;
  path: string;
  isSocket: number;
  sendSocket: boolean;
  requestKind: PartSelectionSaveRequestKind;
  shouldClearActiveInspectId: boolean;
}

/*
 * buildPartSelectionShowMsgConfirmRefAction を partSelection.vue から分離し、
 * WorkAndBuhin の完了メッセージ表示 ref action を固定するため。
 */
export function buildPartSelectionShowMsgConfirmRefAction(): PartSelectionWorkBuhinRefAction {
  return {
    type: "showMsgConfirm"
  };
}

/*
 * onConfirmBuhin を partSelection.vue から分離し、
 * WorkAndBuhin の確定 payload を通常点検保存とおすすめ保存に振り分ける契約を固定するため。
 */
export function buildPartSelectionConfirmSaveState(params: {
  data: any;
  recommendType: number;
  recommendManualType: number;
}): PartSelectionConfirmSaveState {
  const data = params.data || {};
  const seibi = data.seibi || {};
  const isRecommend =
    seibi.sknInspectType == params.recommendType ||
    seibi.sknInspectType == params.recommendManualType;
  return {
    isRecommend,
    editItem: isRecommend ? seibi : null,
    inspectItem: isRecommend ? null : data.inspectItem,
    changeList: data.changeList
  };
}

/*
 * onRegister を partSelection.vue から分離し、
 * 登録未完了時の確認 dialog と登録 event 発火条件を固定するため。
 */
export function buildPartSelectionRegisterActions(params: {
  pageName: any;
  status: any;
  registerNotCompleteStatus: any;
}): PartSelectionRegisterActions {
  const shouldOpenConfirmDialog =
    params.status == params.registerNotCompleteStatus;
  return {
    shouldOpenConfirmDialog,
    shouldEmitSaveSpentTime: !shouldOpenConfirmDialog,
    shouldEmitRegister: !shouldOpenConfirmDialog,
    registerPayload: {
      pageName: params.pageName,
      status: params.status
    }
  };
}

/*
 * onSave を partSelection.vue から分離し、
 * 一時保存時の saveSpentTime event と保存 dialog open を action として固定するため。
 */
export function buildPartSelectionTemporarySaveActions(): PartSelectionTemporarySaveActions {
  return {
    shouldEmitSaveSpentTime: true,
    shouldOpenSaveDialog: true
  };
}

/*
 * onConfirmSave を partSelection.vue から分離し、
 * 未完了登録を確定した後の event payload を固定するため。
 */
export function buildPartSelectionConfirmRegisterActions(params: {
  pageName: any;
  registerNotCompleteStatus: any;
}): PartSelectionConfirmRegisterActions {
  return {
    shouldEmitSaveSpentTime: true,
    shouldEmitRegister: true,
    registerPayload: {
      pageName: params.pageName,
      status: params.registerNotCompleteStatus
    }
  };
}

/*
 * saveInspectItem を partSelection.vue から分離し、
 * 通常点検の savePartSelection 用 item から表示用 field を除去する契約を固定するため。
 */
export function buildPartSelectionInspectSaveItem(params: {
  inspectItem: any;
  changeList: any;
}): any {
  const objForSave = params.inspectItem
    ? JSON.parse(JSON.stringify(params.inspectItem))
    : {};
  delete objForSave.status;
  delete objForSave.showWork;
  if (params.changeList) {
    objForSave.changeList = params.changeList;
  }
  const seibiList = Array.isArray(objForSave.seibiList)
    ? objForSave.seibiList
    : [];
  const manualSeibiList = Array.isArray(objForSave.manualSeibiList)
    ? objForSave.manualSeibiList
    : [];
  seibiList.concat(manualSeibiList).forEach((seibi: any) => {
    // 共通部品UIが補完するおすすめ用項目は PartsSelectionSeibiVO の対象外。
    delete seibi.osusumeBuhinList;
    delete seibi.disabledSave;
  });
  return objForSave;
}

/*
 * saveInspectItem の成功後処理を partSelection.vue から分離し、
 * loading 停止、initData、完了メッセージ表示の action を固定するため。
 */
export function buildPartSelectionSavePartSelectionSuccessActions(params: {
  status: number;
  successStatus: number;
  body: any;
}): PartSelectionSavePartSelectionSuccessActions {
  return {
    shouldStopLoading: true,
    shouldInitData: params.status == params.successStatus && !!params.body,
    shouldShowWorkBuhinConfirm: true
  };
}

/*
 * saveInspectItem の失敗後処理を partSelection.vue から分離し、
 * loading 停止とエラー通知 message を固定するため。
 */
export function buildPartSelectionSavePartSelectionErrorActions(params: {
  inspectItem: any;
}): PartSelectionSavePartSelectionErrorActions {
  const inspect = params.inspectItem || {};
  return {
    shouldStopLoading: true,
    shouldNotifyError: true,
    errorMessage: inspect.sknInspectDescriptionNm
  };
}

/*
 * onSaveInspectResult の前処理を partSelection.vue から分離し、
 * seibiList.inspectResult と buhinList.kousuu を保存前に削除する契約を固定するため。
 */
export function normalizePartSelectionSaveEditItem(editItem: any): any {
  const item = editItem || {};
  const seibiList = Array.isArray(item.seibiList) ? item.seibiList : [];
  seibiList.forEach((seibi: any) => {
    delete seibi.inspectResult;
    const buhinList = Array.isArray(seibi.buhinList) ? seibi.buhinList : [];
    buhinList.forEach((buhin: any) => {
      delete buhin.kousuu;
    });
  });
  return item;
}

/*
 * onSaveInspectResult の request 実行前処理を partSelection.vue から分離し、
 * payload、api path、socket flag、request kind、activeInspectId clear 条件をまとめて固定するため。
 */
export function buildPartSelectionSaveRequestState(params: {
  editItem: any;
  uketsukeExtendsId: any;
  changeList: any;
  circleValue: number;
  doneStatus: number;
  initialStatus: number;
  noNeedStatus: number;
  deleteStatus: number;
  genshaType: number;
  legal56Type: number;
  inspectLineType: number;
  recommendType: number;
  recommendManualType: number;
}): PartSelectionSaveRequestState {
  const editItem = params.editItem || {};
  const requestKind = resolvePartSelectionSaveRequestKind({
    inspectType: editItem.sknInspectType,
    inspectLineType: params.inspectLineType,
    recommendType: params.recommendType,
    recommendManualType: params.recommendManualType
  });
  if (requestKind == "recommend") {
    const recommendSavePayload = buildPartSelectionRecommendSavePayload({
      editItem,
      uketsukeExtendsId: params.uketsukeExtendsId,
      changeList: params.changeList,
      doneStatus: params.doneStatus,
      initialStatus: params.initialStatus,
      deleteStatus: params.deleteStatus,
      recommendManualType: params.recommendManualType
    });
    return {
      data: recommendSavePayload.data,
      path: "gensha",
      isSocket: recommendSavePayload.isSocket,
      sendSocket: false,
      requestKind,
      shouldClearActiveInspectId: false
    };
  }
  if (editItem.hasOwnProperty("changeList")) {
    delete editItem.changeList;
  }
  const normalSavePayload = buildPartSelectionNormalSavePayload({
    editItem,
    circleValue: params.circleValue,
    doneStatus: params.doneStatus,
    noNeedStatus: params.noNeedStatus,
    legal56Type: params.legal56Type,
    inspectLineType: params.inspectLineType
  });
  return {
    data: normalSavePayload.data,
    path: normalSavePayload.path,
    isSocket: normalSavePayload.isSocket,
    sendSocket: true,
    requestKind,
    shouldClearActiveInspectId: normalSavePayload.shouldClearActiveInspectId
  };
}

/*
 * onSaveInspectResult の API 種別判定を partSelection.vue から分離し、
 * inspectLine / recommend / inspect の request kind を固定するため。
 */
export function resolvePartSelectionSaveRequestKind(params: {
  inspectType: number;
  inspectLineType: number;
  recommendType: number;
  recommendManualType: number;
}): PartSelectionSaveRequestKind {
  if (params.inspectType == params.inspectLineType) {
    return "inspectLine";
  }
  if (
    params.inspectType == params.recommendType ||
    params.inspectType == params.recommendManualType
  ) {
    return "recommend";
  }
  return "inspect";
}

/*
 * おすすめ保存前処理を partSelection.vue から分離し、
 * osusumeBuhinList の UI 用 isSelected を payload から除去する契約を固定するため。
 */
export function removePartSelectionRecommendSelectedFlags(saveItem: any): any {
  const item = saveItem || {};
  if (item.osusumeBuhinList && item.osusumeBuhinList.length > 0) {
    item.osusumeBuhinList.forEach((buhin: any) => {
      if (buhin.hasOwnProperty("isSelected")) {
        delete buhin.isSelected;
      }
    });
  }
  return item;
}

/*
 * onSaveInspectResult のおすすめ保存 payload 生成を partSelection.vue から分離し、
 * 既存おすすめ/手入力おすすめの送信形と socket flag を固定するため。
 */
export function buildPartSelectionRecommendSavePayload(params: {
  editItem: any;
  uketsukeExtendsId: any;
  changeList: any;
  doneStatus: number;
  initialStatus: number;
  deleteStatus: number;
  recommendManualType: number;
}): any {
  const editItem = params.editItem || {};
  let isSocket = 0;
  let data: any = {};
  if (editItem.buhinSelected > 0 || editItem.inspectResult == 1) {
    isSocket = 1;
  }
  if (editItem.osusumeId == null && editItem.sknInspectSeibiDetailId == null) {
    const item = {
      sknUketsukeExtendsId: params.uketsukeExtendsId + "",
      seibiId: editItem.sknRecommendInspecSeibiId,
      seibiNm: editItem.sknRecommendInspecSeibiNm,
      buhinList: editItem.buhinList,
      kouchinZn: editItem.kouchinZn,
      buhinSelected:
        typeof editItem.buhinSelected !== "undefined"
          ? editItem.osusumeIsShow == 0
            ? params.deleteStatus
            : editItem.buhinSelected
          : params.doneStatus,
      sknInspectType: params.recommendManualType,
      sknRecommendInspectCategoryId: editItem.sknRecommendInspectCategoryId,
      osusumeIsShow:
        typeof editItem.osusumeIsShow !== "undefined"
          ? editItem.osusumeIsShow
          : 1
    };
    data = {
      recommend: [
        {
          manualItems: [item]
        }
      ]
    };
  } else {
    let saveItem = JSON.parse(JSON.stringify(editItem));
    saveItem.buhinSelected =
      editItem.osusumeIsShow == 0
        ? params.initialStatus
        : editItem.buhinSelected;
    saveItem = removePartSelectionRecommendSelectedFlags(saveItem);
    data = {
      recommend: [
        {
          subItems: [saveItem]
        }
      ]
    };
  }
  data.isSocket = isSocket;
  if (params.changeList) {
    data.changeList = params.changeList;
  }
  return {
    data,
    isSocket
  };
}

/*
 * 通常点検保存前処理を partSelection.vue から分離し、
 * buhinList/freeOfChargeBuhinList の canJunseiFree を保存前に削除する契約を固定するため。
 */
export function removePartSelectionCanJunseiFree(items: any): any {
  const seibiList = items && Array.isArray(items.seibiList)
    ? items.seibiList
    : [];
  seibiList.forEach((seibi: any) => {
    if (seibi.buhinList && seibi.buhinList.length > 0) {
      seibi.buhinList.forEach((buhin: any) => {
        delete buhin.canJunseiFree;
      });
    }
    if (seibi.freeOfChargeBuhinList && seibi.freeOfChargeBuhinList.length > 0) {
      seibi.freeOfChargeBuhinList.forEach((buhin: any) => {
        delete buhin.canJunseiFree;
      });
    }
  });
  return items;
}

/*
 * onSaveInspectResult の通常点検保存 payload 生成を partSelection.vue から分離し、
 * gensha/legal56/inspectLine の path、socket flag、manualSeibiList 整形を固定するため。
 */
export function buildPartSelectionNormalSavePayload(params: {
  editItem: any;
  circleValue: number;
  doneStatus: number;
  noNeedStatus: number;
  legal56Type: number;
  inspectLineType: number;
}): PartSelectionNormalSavePayloadState {
  const editItem = params.editItem || {};
  let isSocket = 0;
  let path = "gensha";
  const data: any = {};
  const shouldClearActiveInspectId = editItem.inspectResult == params.circleValue;
  if (
    editItem.workBuhinSelected == params.doneStatus ||
    editItem.workBuhinSelected == params.noNeedStatus
  ) {
    isSocket = 1;
  }
  if (editItem.sknInspectType == params.legal56Type) {
    path = "legal56";
    data.isSocket = isSocket;
  } else if (editItem.sknInspectType == params.inspectLineType) {
    path = "inspectLine";
  } else {
    data.isSocket = isSocket;
  }
  removePartSelectionCanJunseiFree(editItem);
  if (editItem.manualSeibiList && editItem.manualSeibiList.length > 0) {
    const listFiltered = editItem.manualSeibiList.filter(
      (seibi: any) => seibi.buhinSelected > 0
    );
    if (listFiltered.length > 0) {
      editItem.manualSeibiList = listFiltered;
    }
  }
  data[path] = [
    {
      subItems: [
        {
          inspectDescriptions: [editItem]
        }
      ]
    }
  ];
  return {
    data,
    path,
    isSocket,
    shouldClearActiveInspectId
  };
}

/*
 * removeInspectParam を partSelection.vue から分離し、
 * 保存用点検 item の disabledSave/ignoreSeibi/数量なし部品の除去を固定するため。
 */
export function normalizePartSelectionInspectParamForSave(params: {
  inspectItem: any;
  initialStatus: number;
  doneStatus: number;
  fakeDoneStatus: number;
  genshaType: number;
  legal56Type: number;
}): any {
  const objForSave = params.inspectItem || {};
  if (objForSave.workBuhinSelected == params.fakeDoneStatus) {
    objForSave.workBuhinSelected = params.doneStatus;
  }
  const seibiList = Array.isArray(objForSave.seibiList)
    ? objForSave.seibiList
    : [];
  for (let i = seibiList.length - 1; i >= 0; i--) {
    const seibi = seibiList[i];
    if (
      seibi.buhinSelected == params.initialStatus &&
      seibi.seibiId == null
    ) {
      seibiList.splice(i, 1);
    } else if (seibi.disabledSave) {
      if (
        objForSave.sknUketsukeInspectId == null &&
        objForSave.workBuhinSelected == params.doneStatus
      ) {
        if (
          seibi.sknInspectType == params.genshaType ||
          seibi.sknInspectType == params.legal56Type
        ) {
          seibiList.splice(i, 1);
        } else {
          delete seibi.disabledSave;
        }
      } else {
        seibiList.splice(i, 1);
      }
    } else {
      const buhinList = Array.isArray(seibi.buhinList)
        ? seibi.buhinList.filter((buhin: any) => buhin.quantity > 0)
        : [];
      seibi.buhinList = buhinList;
      if (seibi.hasOwnProperty("disabledSave")) {
        delete seibi.disabledSave;
      }
      if (seibi.hasOwnProperty("ignoreSeibi")) {
        delete seibi.ignoreSeibi;
      }
    }
  }
  objForSave.seibiList = seibiList;
  return objForSave;
}

/*
 * removeInspectParam の CIRCLE 時削除対象抽出を partSelection.vue から分離し、
 * 手入力整備 ID と手入力部品 ID の収集条件を固定するため。
 */
export function collectPartSelectionCircleDeleteTargets(
  inspectItem: any
): PartSelectionDeleteTargets {
  const item = inspectItem || {};
  const manualSeibiList = Array.isArray(item.manualSeibiList)
    ? item.manualSeibiList
    : [];
  const seibiList = Array.isArray(item.seibiList) ? item.seibiList : [];
  const manualBuhinIds: any[] = [];
  seibiList.forEach((seibi: any) => {
    const manualBuhinList = Array.isArray(seibi.manualBuhinList)
      ? seibi.manualBuhinList
      : [];
    manualBuhinList.forEach((buhin: any) => {
      if (buhin.buhinId) {
        manualBuhinIds.push(buhin.buhinId);
      }
    });
  });
  return {
    manualSeibiIds: manualSeibiList
      .filter((seibi: any) => seibi.seibiId)
      .map((seibi: any) => seibi.seibiId),
    manualBuhinIds
  };
}

/*
 * removeInspectParam の CIRCLE 時 API 削除境界を partSelection.vue から分離し、
 * manual item 削除 API を呼ぶ条件と対象 ID を固定するため。
 */
export function buildPartSelectionCircleDeleteApiActions(
  inspectItem: any
): PartSelectionCircleDeleteApiActions {
  const item = inspectItem || {};
  const shouldDeleteManualItems = item.workBuhinSelected != 0;
  return {
    shouldDeleteManualItems,
    deleteTargets: shouldDeleteManualItems
      ? collectPartSelectionCircleDeleteTargets(item)
      : {
          manualSeibiIds: [],
          manualBuhinIds: []
        }
  };
}

/*
 * removeInspectParam の CIRCLE 時 state 変換を partSelection.vue から分離し、
 * workBuhinSelected、manualSeibiList、通常部品 isDelete の更新を固定するため。
 */
export function applyPartSelectionCircleDeleteState(params: {
  inspectItem: any;
  noNeedStatus: number;
  initialStatus: number;
}): any {
  const item = params.inspectItem || {};
  const oldWorkBuhinSelected = item.workBuhinSelected;
  item.workBuhinSelected = params.noNeedStatus;
  if (oldWorkBuhinSelected == 0) {
    item.seibiList = [];
    return item;
  }

  if (Array.isArray(item.manualSeibiList) && item.manualSeibiList.length > 0) {
    item.manualSeibiList = [];
  }
  const seibiList = Array.isArray(item.seibiList) ? item.seibiList : [];
  seibiList.forEach((seibi: any) => {
    seibi.buhinSelected = params.initialStatus;
    const buhinList = Array.isArray(seibi.buhinList) ? seibi.buhinList : [];
    buhinList.forEach((buhin: any) => {
      if (buhin.id) {
        buhin.isDelete = 1;
      }
    });
  });
  item.seibiList = seibiList;
  return item;
}

/*
 * onInspectBtnChange を partSelection.vue から分離し、
 * 判定ボタン変更時の inspectResult/osusumeIsShow と正規化要否を固定するため。
 */
export function buildPartSelectionInspectButtonChangeState(params: {
  item: any;
  result: number;
  recommendType: number;
  recommendManualType: number;
  circleValue: number;
  noNeedStatus: number;
}): PartSelectionInspectButtonChangeState {
  const item = params.item || {};
  if (
    item.sknInspectType == params.recommendType ||
    item.sknInspectType == params.recommendManualType
  ) {
    return {
      itemState: {
        osusumeIsShow: params.result
      },
      shouldNormalizeInspectParam: false
    };
  }
  return {
    itemState: {
      inspectResult: params.result,
      ...(params.result === params.circleValue
        ? {
            workBuhinSelected: params.noNeedStatus
          }
        : {})
    },
    shouldNormalizeInspectParam: params.result === params.circleValue
  };
}

/*
 * onEditResult を partSelection.vue から分離し、
 * 編集 dialog の title、表示状態、deep copy、hideTriangle 判定を固定するため。
 */
export function buildPartSelectionEditDialogState(params: {
  item: any;
  isNoSeibi: boolean;
  recommendType: number;
  recommendManualType: number;
}): PartSelectionEditDialogState {
  const item = params.item || {};
  const isRecommend =
    item.sknInspectType == params.recommendType ||
    item.sknInspectType == params.recommendManualType;
  return {
    editTitle: item.sknInspectDescriptionNm || item.sknRecommendInspecSeibiNm,
    displayDialogEdit: true,
    editItem: JSON.parse(JSON.stringify(item)),
    hideTriangle: isRecommend ? false : params.isNoSeibi
  };
}

/*
 * inspectSuccess を partSelection.vue から分離し、
 * 通常点検の部品選択完了/部品なし完了の template 判定を固定するため。
 */
export function isPartSelectionInspectSuccess(params: {
  inspect: any;
  doneStatus: number;
  noNeedStatus: number;
}): boolean {
  const inspect = params.inspect || {};
  return (
    inspect.workBuhinSelected == params.doneStatus ||
    inspect.workBuhinSelected == params.noNeedStatus
  );
}

/*
 * seibiRecSuccess を partSelection.vue から分離し、
 * おすすめカテゴリ内に未選択 item が残っていないかの template 判定を固定するため。
 */
export function isPartSelectionRecommendSuccess(params: {
  recommendCategory: any;
  initialStatus: number;
}): boolean {
  const category = params.recommendCategory || {};
  const subItems = Array.isArray(category.subItems) ? category.subItems : [];
  return subItems.filter((seibi: any) => seibi.buhinSelected == params.initialStatus)
    .length == 0;
}

/*
 * showTitle を partSelection.vue から分離し、
 * 点検カテゴリ見出しを表示するかどうかの template 判定を固定するため。
 */
export function shouldShowPartSelectionTitle(items: any): boolean {
  return Array.isArray(items) && items.length > 0;
}

/*
 * isNoSeibi を partSelection.vue から分離し、
 * 固定文言と inspectService 判定結果から整備なし扱いを決める契約を固定するため。
 */
export function isPartSelectionNoSeibi(params: {
  inspect: any;
  inspectServiceNoSeibi: boolean;
  noNeedStatus: number;
  fixedNoSeibiNames: string[];
}): boolean {
  const inspect = params.inspect || {};
  const name = (inspect.sknInspectDescriptionNm || "").trim();
  if (params.fixedNoSeibiNames.indexOf(name) > -1) {
    return true;
  }
  return (
    params.inspectServiceNoSeibi &&
    inspect.workBuhinSelected == params.noNeedStatus
  );
}

/*
 * recommendLength を partSelection.vue から分離し、
 * subItems を持つおすすめカテゴリ数の算出を固定するため。
 */
export function getPartSelectionRecommendLength(listRecommend: any): number {
  const list = Array.isArray(listRecommend) ? listRecommend : [];
  return list.filter(
    (item: any) => item.subItems && item.subItems.length > 0
  ).length;
}

/*
 * onCallbackDeleteManualSeibi を partSelection.vue から分離し、
 * 手入力整備削除後に socket 通知する条件を固定するため。
 */
export function shouldPartSelectionSendManualSeibiDeleteSocket(
  sknUketsukeInspectId: any
): boolean {
  return !!sknUketsukeInspectId;
}

/*
 * onSaveInspectResult 成功後の socket 判定を partSelection.vue から分離し、
 * API payload 側の isSocket と通常保存側の sendSocket の両方を見る契約を固定するため。
 */
export function shouldPartSelectionSendSaveSocket(params: {
  isSocket: number;
  sendSocket: boolean;
}): boolean {
  return !!params.isSocket && params.sendSocket;
}

/*
 * callbackDeleteBuhin を partSelection.vue から分離し、
 * WorkAndBuhin から返る削除 callback data を editItem と保存要否に変換するため。
 */
export function buildPartSelectionDeleteBuhinCallbackState(
  data: any
): PartSelectionDeleteBuhinCallbackState {
  // おすすめ一覧全体には点検種別がないため、削除した部品の所属作業を保存する。
  const work = data && data.work;
  const isRecommend = work && (
    work.sknInspectType == INSPECT_TYPE.RECOMMEND ||
    work.sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL
  );
  const source = isRecommend ? work : data && data.data;
  const editItem = source ? { ...source } : {};
  if (editItem.hasOwnProperty("changeList")) {
    delete editItem.changeList;
  }
  return {
    editItem,
    shouldSaveInspectResult: true
  };
}

/*
 * onSaveInspectResult の成功後処理を partSelection.vue から分離し、
 * socket、再読込、WorkAndBuhin 完了メッセージの action を固定するため。
 */
export function buildPartSelectionSaveSuccessActions(params: {
  status: number;
  successStatus: number;
  isSocket: number;
  sendSocket: boolean;
  isWorkBuhin: boolean;
}): PartSelectionSaveSuccessActions {
  const isSuccess = params.status == params.successStatus;
  return {
    shouldSendSocket:
      isSuccess &&
      shouldPartSelectionSendSaveSocket({
        isSocket: params.isSocket,
        sendSocket: params.sendSocket
      }),
    shouldReload: isSuccess,
    shouldShowWorkBuhinConfirm: isSuccess && params.isWorkBuhin
  };
}

/*
 * onSaveInspectResult の loading 制御を partSelection.vue から分離し、
 * WorkAndBuhin 起点保存時だけ loading を開始/停止する契約を固定するため。
 */
export function buildPartSelectionSaveLoadingActions(params: {
  isWorkBuhin: boolean;
}): PartSelectionSaveLoadingActions {
  return {
    shouldStartLoading: params.isWorkBuhin,
    shouldStopLoadingAfterRequestSetup: params.isWorkBuhin
  };
}

/*
 * initData の表示可否 ref action を partSelection.vue から分離し、
 * 点検/おすすめがすべて対象なしの場合に hideBuhin する条件を固定するため。
 */
export function buildPartSelectionDataVisibilityRefAction(params: {
  inspectList: any;
  listRecommend: any;
  circleValue: number;
}): PartSelectionWorkBuhinRefAction {
  const inspectList = Array.isArray(params.inspectList)
    ? params.inspectList
    : [];
  const listRecommend = Array.isArray(params.listRecommend)
    ? params.listRecommend
    : [];
  if (inspectList.length == 0 && listRecommend.length == 0) {
    return {
      type: "hideBuhin"
    };
  }
  const allNoItem = listRecommend.every(
    (item: any) => !item.subItems || item.subItems.length == 0
  );
  const noInspect =
    inspectList.length == 0 ||
    inspectList.every((item: any) => item.inspectResult == params.circleValue);
  return noInspect && allNoItem
    ? {
        type: "hideBuhin"
      }
    : {
        type: "none"
      };
}

/*
 * 通常点検クリック時の選択 state を partSelection.vue から分離し、
 * 通常整備/手入力整備/整備なしごとの WorkAndBuhin ref action を固定するため。
 */
export function buildPartSelectionInspectSelectState(params: {
  inspect: any;
  initialBuhinStatus: number;
}): PartSelectionSelectState {
  const inspect = params.inspect || {};
  const seibiList = Array.isArray(inspect.seibiList) ? inspect.seibiList : [];
  const manualSeibiList = Array.isArray(inspect.manualSeibiList)
    ? inspect.manualSeibiList
    : [];
  if (seibiList.length > 0) {
    let index = seibiList.findIndex(
      (seibi: any) => seibi.buhinSelected != params.initialBuhinStatus
    );
    if (index == -1) {
      index = 0;
    }
    return {
      isInspect: true,
      activeInspect: inspect,
      activeInspectId: inspect.sknInspectDescriptionId,
      refAction: {
        type: "selectWork",
        work: seibiList[index],
        index,
        isManual: false,
        forceSelect: true
      }
    };
  }
  if (manualSeibiList.length > 0) {
    return {
      isInspect: true,
      activeInspect: inspect,
      activeInspectId: inspect.sknInspectDescriptionId,
      refAction: {
        type: "selectWork",
        work: manualSeibiList[0],
        index: 0,
        isManual: true,
        forceSelect: true
      }
    };
  }
  return {
    isInspect: true,
    activeInspect: inspect,
    activeInspectId: inspect.sknInspectDescriptionId,
    refAction: {
      type: "noSeibi"
    }
  };
}

/*
 * おすすめカテゴリクリック時の選択 state を partSelection.vue から分離し、
 * subItems を疑似 seibiList として WorkAndBuhin に渡す契約を固定するため。
 */
export function buildPartSelectionRecommendSelectState(
  recCate: any
): PartSelectionSelectState {
  const category = recCate || {};
  const seibiList = Array.isArray(category.subItems) ? category.subItems : [];
  const activeInspect: PartSelectionRecommendWorkAndBuhinActiveData = {
    sknRecommendInspectCategoryId: category.sknRecommendInspectCategoryId,
    sknRecommendInspectCategoryNm: category.sknRecommendInspectCategoryNm,
    status: category.status,
    kingakuZn: category.kingakuZn,
    kingakuZk: category.kingakuZk,
    subItems: seibiList,
    seibiList
  };
  return {
    isInspect: false,
    activeInspect,
    activeInspectId: category.sknRecommendInspectCategoryId,
    refAction:
      seibiList.length > 0
        ? {
            type: "selectWork",
            work: seibiList[0],
            index: 0,
            isManual: false,
            forceSelect: true
          }
        : {
            type: "none"
          }
  };
}

/*
 * 初回読み込み時の選択 state を partSelection.vue から分離し、
 * 通常点検、部品なし点検、おすすめ、対象なしの優先順を固定するため。
 */
export function buildPartSelectionFirstSelectState(params: {
  listInspectByResult: any;
  listRecommend: any;
  initialBuhinStatus: number;
}): PartSelectionSelectState {
  const listInspectByResult = Array.isArray(params.listInspectByResult)
    ? params.listInspectByResult
    : [];
  if (listInspectByResult.length > 0 && listInspectByResult[0].length > 0) {
    return buildPartSelectionInspectSelectState({
      inspect: listInspectByResult[0][0],
      initialBuhinStatus: params.initialBuhinStatus
    });
  }
  if (listInspectByResult.length > 1 && listInspectByResult[1].length > 0) {
    return buildPartSelectionInspectSelectState({
      inspect: listInspectByResult[1][0],
      initialBuhinStatus: params.initialBuhinStatus
    });
  }
  const listRecommend = Array.isArray(params.listRecommend)
    ? params.listRecommend
    : [];
  const allNoItem = listRecommend.every(
    (item: any) => !item.subItems || item.subItems.length == 0
  );
  if (!allNoItem && listRecommend.length > 0) {
    return buildPartSelectionRecommendSelectState(listRecommend[0]);
  }
  return {
    isInspect: false,
    activeInspect: {},
    activeInspectId: -1,
    refAction: {
      type: "resetBuhin"
    }
  };
}

/*
 * 保存後再読込時の選択復元 state を partSelection.vue から分離し、
 * activeInspectId と通常/おすすめ mode から reInitBuhin 対象を探す契約を固定するため。
 */
export function buildPartSelectionReloadSelectState(params: {
  activeInspectId: any;
  isInspect: boolean;
  inspectList: any;
  listRecommend: any;
}): PartSelectionSelectState | null {
  if (params.activeInspectId <= -1) {
    return null;
  }
  if (params.isInspect) {
    const inspectList = Array.isArray(params.inspectList)
      ? params.inspectList
      : [];
    const foundInspect = inspectList.find(
      (inspect: any) =>
        inspect.sknInspectDescriptionId == params.activeInspectId
    );
    return foundInspect
      ? {
          isInspect: true,
          activeInspect: foundInspect,
          activeInspectId: params.activeInspectId,
          refAction: {
            type: "reInitBuhin",
            data: foundInspect
          }
        }
      : null;
  }
  const listRecommend = Array.isArray(params.listRecommend)
    ? params.listRecommend
    : [];
  const found = listRecommend.find(
    (item: any) => item.sknRecommendInspectCategoryId == params.activeInspectId
  );
  if (!found) {
    return null;
  }
  const activeInspect = {
    ...found,
    seibiList: found.subItems
  };
  return {
    isInspect: false,
    activeInspect,
    activeInspectId: params.activeInspectId,
    refAction: {
      type: "reInitBuhin",
      data: activeInspect
    }
  };
}

/*
 * initData の選択 state を partSelection.vue から分離し、
 * 初回選択、再読込復元、復元失敗時 fallback の分岐を固定するため。
 */
export function buildPartSelectionInitSelectState(params: {
  first: boolean;
  activeInspectId: any;
  isInspect: boolean;
  listInspectByResult: any;
  inspectList: any;
  listRecommend: any;
  initialBuhinStatus: number;
}): PartSelectionSelectState {
  if (params.first) {
    return buildPartSelectionFirstSelectState({
      listInspectByResult: params.listInspectByResult,
      listRecommend: params.listRecommend,
      initialBuhinStatus: params.initialBuhinStatus
    });
  }
  const reloadState = buildPartSelectionReloadSelectState({
    activeInspectId: params.activeInspectId,
    isInspect: params.isInspect,
    inspectList: params.inspectList,
    listRecommend: params.listRecommend
  });
  return reloadState || buildPartSelectionFirstSelectState({
    listInspectByResult: params.listInspectByResult,
    listRecommend: params.listRecommend,
    initialBuhinStatus: params.initialBuhinStatus
  });
}

/*
 * initData の ref action 生成を partSelection.vue から分離し、
 * visibility action と選択 state を同時に返す順序を固定するため。
 */
export function buildPartSelectionInitRefActions(params: {
  first: boolean;
  activeInspectId: any;
  isInspect: boolean;
  listInspectByResult: any;
  inspectList: any;
  listRecommend: any;
  initialBuhinStatus: number;
  circleValue: number;
}): PartSelectionInitRefActions {
  return {
    visibilityAction: buildPartSelectionDataVisibilityRefAction({
      inspectList: params.inspectList,
      listRecommend: params.listRecommend,
      circleValue: params.circleValue
    }),
    selectState: buildPartSelectionInitSelectState({
      first: params.first,
      activeInspectId: params.activeInspectId,
      isInspect: params.isInspect,
      listInspectByResult: params.listInspectByResult,
      inspectList: params.inspectList,
      listRecommend: params.listRecommend,
      initialBuhinStatus: params.initialBuhinStatus
    })
  };
}
